import React, { FC, useMemo, useCallback } from "react";
import { animated, useTransition } from "@react-spring/web";

interface LoadingProps {
  isLoading: boolean;
  progress: number;
  message?: string;
  className?: string;
  onRetry?: () => void;
  error?: string;
}

const Loading: FC<LoadingProps> = React.memo(
  ({
    isLoading,
    progress,
    message = "Loading...",
    className = "",
    onRetry,
    error,
  }) => {
    // Memoize transition configuration
    const transitionConfig = useMemo(
      () => ({
        from: { opacity: 0, transform: "translateY(-20px)" },
        enter: { opacity: 1, transform: "translateY(0px)" },
        leave: { opacity: 0, transform: "translateY(-20px)" },
        config: { tension: 220, friction: 20 },
      }),
      [],
    );

    // Create transition with memoized configuration
    const transitions = useTransition(isLoading || error, transitionConfig);

    return transitions(
      (styles, item) =>
        item && (
          <animated.div
            style={styles}
            className={`absolute inset-0 flex flex-col items-center justify-center backdrop--blur z-50 ${className}`}
            aria-busy={isLoading}
            aria-label="Loading"
          >
            {error ? (
              <>
                <div className="mb-4 text-red-500 text-xl">Error: {error}</div>
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="mt-2 px-4 py-2 bg-red-500 text-white rounded"
                  >
                    Retry
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="mb-1 text-white text-xl">{message}</div>
                <div className="w-64 h-2 bg-gray-300 rounded-full overflow-hidden">
                  <animated.div
                    style={{
                      width: `${progress}%`,
                    }}
                    className="h-full bg-lavenderBlush"
                  />
                </div>
                <div className="mt-1 text-white text-sm">{progress}%</div>
              </>
            )}
          </animated.div>
        ),
    );
  },
);

export default Loading;
